import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { loadTurnstile, removeTurnstile, renderTurnstile, resetTurnstile } from '@/lib/turnstile';
import { track } from '@/lib/track';
import * as metaPixel from '@/lib/metaPixel';
import { LEAD_COPY, TURNSTILE_SITE_KEY } from '@/marketing/template-lead';
import { submitTemplateLead } from '@/services/api/templateLeads.service';

/** `blocked`: Cloudflare's check cannot run here (a blocker, a CSP or hostname error): the form is off and the PDF is the way. */
type State = 'idle' | 'sending' | 'done' | 'error' | 'blocked';

/** After this many presses of the button with no token, the check is taken as blocked. */
const PENDING_LIMIT = 3;

/** The machine code of an error body: `{ code }` or `{ errors: { code } }`. */
function codeOf(data: unknown): string | undefined {
  const body = data && typeof data === 'object' ? (data as { code?: unknown; errors?: { code?: unknown } }) : {};
  const code = body.code ?? body.errors?.code;
  return typeof code === 'string' ? code : undefined;
}

/** The plain message for a failed submit: never the raw error, never anything about the address. */
function messageFor(err: unknown): { text: string; botCheck: boolean; email: boolean } {
  const res = (err as { response?: { status?: number; data?: unknown } } | null)?.response;
  const code = codeOf(res?.data);
  const e = LEAD_COPY.errors;
  if (res?.status === 422 && code === 'invalid_email') return { text: e.invalidEmail, botCheck: false, email: true };
  if (res?.status === 422 && code === 'bot_check_failed') return { text: e.botCheck, botCheck: true, email: false };
  if (res?.status === 429) return { text: e.tooMany, botCheck: false, email: false };
  if (res?.status === 503) return { text: e.unavailable, botCheck: false, email: false };
  return { text: e.generic, botCheck: false, email: false };
}

/**
 * The email form under the PDF button of an estimate or quote template page: a second offer (the PDF stays free, with no sign-up) that emails the
 * visitor the editable Google Sheet. English only (US). The copy is DRAFT and lives in LEAD_COPY (src/marketing/template-lead.ts). Cloudflare's
 * script loads only when the field is focused, so the page's first load carries nothing extra. Analytics events carry the template slug and a step,
 * never the address or the token.
 */
export function TemplateLeadForm({ template, page, utm, labelledBy, notice = LEAD_COPY.consent }: { template: string; /** the ad landing page passes 'funnel' and the ad source; the template pages pass neither (their request is unchanged) */ page?: string; utm?: Record<string, string>; /** the page has its own h2 for the form: no heading here, the field is named by that id */ labelledBy?: string; /** the one-line notice under the field; the ad landing page passes its own shorter one, the template pages keep LEAD_COPY.consent */ notice?: string }) {
  const uid = useId();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<State>('idle');
  const [message, setMessage] = useState('');
  const token = useRef('');
  const widget = useRef<string | undefined>(undefined);
  const box = useRef<HTMLDivElement>(null);
  const form = useRef<HTMLFormElement>(null);
  const field = useRef<HTMLInputElement>(null);
  const pot = useRef<HTMLInputElement>(null);
  const done = useRef<HTMLParagraphElement>(null);
  const started = useRef(false);
  const sending = useRef(false);
  const pending = useRef(0);

  // "form viewed": once, when it scrolls into view (no Turnstile script is loaded for this)
  useEffect(() => {
    const el = form.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        track('Template lead', { template, step: 'viewed' });
        io.disconnect();
      }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [template]);

  // after sending, the form is gone: put focus on the thank-you so keyboard and screen reader users are not dropped at the top of the page
  useEffect(() => {
    if (state === 'done') done.current?.focus();
  }, [state]);

  // the widget goes with the form
  useEffect(
    () => () => {
      removeTurnstile(widget.current);
    },
    [],
  );

  const block = () => {
    token.current = '';
    setState('blocked');
  };

  const startWidget = () => {
    if (started.current) return;
    started.current = true;
    loadTurnstile()
      .then(() => {
        if (box.current) {
          widget.current = renderTurnstile(
            box.current,
            TURNSTILE_SITE_KEY,
            (t) => {
              token.current = t;
            },
            () => {
              token.current = '';
            },
            block,
          );
        }
      })
      .catch(block);
  };

  const onFocus = () => {
    if (!started.current) track('Template lead', { template, step: 'started' });
    startWidget();
  };

  const fail = (text: string) => {
    setMessage(text);
    setState('error');
  };

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (sending.current || state === 'blocked') return;
    if (!token.current) {
      pending.current += 1;
      if (pending.current >= PENDING_LIMIT) return block();
      startWidget();
      fail(LEAD_COPY.errors.checkPending);
      return;
    }
    pending.current = 0;
    const website = pot.current?.value ?? '';
    sending.current = true;
    setState('sending');
    try {
      await submitTemplateLead({ email: email.trim(), template, token: token.current, website, ...(page ? { page } : {}), ...utm });
      track('Template lead', { template, step: 'submitted' });
      // the Meta Pixel's conversion (only with analytics consent, on an allow-listed page): the template slug, never the address
      metaPixel.track('Lead', { content_name: template, content_category: 'template' });
      removeTurnstile(widget.current);
      widget.current = undefined;
      setState('done');
    } catch (err) {
      const m = messageFor(err);
      // a token is good for one try: ask for a new one whatever the answer was
      token.current = '';
      try {
        resetTurnstile(widget.current);
      } catch {
        /* the widget is gone: the next submit says the check is loading and starts it again */
      }
      fail(m.text);
      if (m.email) field.current?.focus();
    } finally {
      sending.current = false;
    }
  };

  if (state === 'done') {
    return (
      <p ref={done} tabIndex={-1} role="status" className="mt-4 rounded-xl border border-slate-300 bg-white p-4 text-lg text-slate-800 outline-none">
        {LEAD_COPY.success}
      </p>
    );
  }
  const headingId = `${uid}-h`;
  const labelId = `${uid}-l`;
  const errorId = `${uid}-e`;
  return (
    <form ref={form} data-template-lead-form onSubmit={submit} className="relative mt-4 rounded-xl border border-slate-300 bg-white p-4 shadow-sm sm:p-5">
      {!labelledBy && <h3 id={headingId} className="mb-2 text-lg font-semibold text-slate-900">{LEAD_COPY.heading}</h3>}
      <div className="flex flex-col gap-3 sm:flex-row">
        <label id={labelId} htmlFor={`${uid}-email`} className="sr-only">{LEAD_COPY.emailLabel}</label>
        <input
          ref={field}
          id={`${uid}-email`}
          name="email"
          type="email"
          inputMode="email"
          required
          maxLength={254}
          autoComplete="email"
          aria-labelledby={`${labelledBy ?? headingId} ${labelId}`}
          aria-describedby={errorId}
          aria-invalid={state === 'error' && message === LEAD_COPY.errors.invalidEmail ? true : undefined}
          value={email}
          onFocus={onFocus}
          onChange={(ev) => setEmail(ev.target.value)}
          className="min-h-[44px] flex-1 rounded-lg border border-slate-300 px-3 text-lg"
          placeholder={LEAD_COPY.placeholder}
        />
        <Button type="submit" size="lg" disabled={state === 'sending' || state === 'blocked'}>{state === 'sending' ? LEAD_COPY.sending : LEAD_COPY.button}</Button>
      </div>
      <div ref={box} />
      <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}>
        <input ref={pot} type="text" name="website" tabIndex={-1} autoComplete="off" defaultValue="" />
      </div>
      {/* always in the page, so a message appearing moves nothing and is announced when it is filled in */}
      <p id={errorId} role="alert" className="mt-2 min-h-[1.25rem] text-sm text-red-700">{state === 'blocked' ? LEAD_COPY.errors.blocked : state === 'error' ? message : ''}</p>
      <p className="mt-1 text-sm text-slate-600">{notice}</p>
    </form>
  );
}
